/**
 * feedbackAdjustment.ts
 *
 * Drop-in module to blend community feedback into your existing player ranking score.
 * - Uses weighted sentiment with Bayesian shrinkage (toward 0) so small N barely moves ranks
 * - Trust-weighted votes so credible users matter more
 * - Diminishing returns via tanh() and sqrt(N)
 * - Hard caps so no one gets yanked too far
 * - Optional recency decay and role-based filters
 *
 * Integrate by calling adjustPlayerScore(baseScore, feedback, options) and
 * add the returned delta to your formula's base score (or translate to rank spots).
 */

export type VoteValue = -1 | 0 | 1; // -1 = "too high", 0 = "about right", 1 = "too low"

export interface PlayerVote {
  userId: string;
  value: VoteValue;
  /** 0..1 trust; default 0.5 if unknown */
  trust?: number;
  /** Unix ms; used with recencyHalfLifeDays */
  timestamp?: number;
}

export interface AdjustmentOptions {
  /** Max absolute movement in baseScore units (e.g., Elo points or rank-spots if your score is rank) */
  maxMove?: number; // default: 6
  /** Global weight scaling for how responsive the model is to sentiment */
  responsiveness?: number; // default: 1.0
  /** Prior sample size that shrinks sentiment toward 0 (no change). Higher = more conservative. */
  priorN?: number; // default: 30
  /** Minimum effective N before applying any change */
  minEffectiveN?: number; // default: 5
  /** Soft threshold for consensus: multiplier based on agreement beyond 50% */
  consensusBoost?: {
    /** Strength of boost; 0 disables */
    strength: number; // default: 0.6
    /** Minimum agreement to start boosting (0.5-1). 0.6 means >=60% leaning one way */
    start: number; // default: 0.6
  };
  /** Recency decay half-life in days; undefined disables */
  recencyHalfLifeDays?: number; // default: 90
  /** Cap a single user's weight to avoid whales */
  perUserWeightCap?: number; // default: 1.0
  /** Optional: floor/ceil for trust coming in */
  trustFloor?: number; // default: 0.2
  trustCeil?: number; // default: 1.0
  /** Nonlinearity gain for tanh mapping */
  nonlinearityK?: number; // default: 1.25
}

export interface AdjustmentResult {
  adjustedScore: number;
  delta: number; // added to baseScore
  debug: {
    weightedMean: number; // sentiment in [-1,1]
    effectiveN: number;
    priorN: number;
    shrunkSentiment: number;
    agreement: number; // max(pLow, pHigh)
    consensusMultiplier: number;
    rawDelta: number;
    cappedDelta: number;
  };
}

export function adjustPlayerScore(
  baseScore: number,
  votes: PlayerVote[],
  options: AdjustmentOptions = {}
): AdjustmentResult {
  // Default options
  const opts = {
    maxMove: options.maxMove ?? 6,
    responsiveness: options.responsiveness ?? 1.0,
    priorN: options.priorN ?? 30,
    minEffectiveN: options.minEffectiveN ?? 5,
    consensusBoost: {
      strength: options.consensusBoost?.strength ?? 0.6,
      start: options.consensusBoost?.start ?? 0.6,
    },
    recencyHalfLifeDays: options.recencyHalfLifeDays ?? 90,
    perUserWeightCap: options.perUserWeightCap ?? 1.0,
    trustFloor: options.trustFloor ?? 0.2,
    trustCeil: options.trustCeil ?? 1.0,
    nonlinearityK: options.nonlinearityK ?? 1.25,
  };

  // If no votes, no adjustment
  if (!votes || votes.length === 0) {
    return {
      adjustedScore: baseScore,
      delta: 0,
      debug: {
        weightedMean: 0,
        effectiveN: 0,
        priorN: opts.priorN,
        shrunkSentiment: 0,
        agreement: 0,
        consensusMultiplier: 1,
        rawDelta: 0,
        cappedDelta: 0,
      },
    };
  }

  const now = Date.now();
  let totalWeight = 0;
  let weightedSum = 0;

  // Count votes by sentiment for consensus calculation
  let negativeVotes = 0; // "too high"
  let neutralVotes = 0;  // "about right"
  let positiveVotes = 0; // "too low"

  // Process each vote
  for (const vote of votes) {
    // Trust clamping
    let trust = vote.trust ?? 0.5;
    trust = Math.max(opts.trustFloor, Math.min(opts.trustCeil, trust));

    // Recency decay
    let recencyWeight = 1;
    if (opts.recencyHalfLifeDays && vote.timestamp) {
      const ageInDays = (now - vote.timestamp) / (1000 * 60 * 60 * 24);
      const halfLife = opts.recencyHalfLifeDays;
      recencyWeight = Math.exp((-ageInDays * Math.LN2) / halfLife);
    }

    // Final weight for this vote
    const voteWeight = Math.min(trust * recencyWeight, opts.perUserWeightCap);

    totalWeight += voteWeight;
    weightedSum += vote.value * voteWeight;

    // Count for consensus
    if (vote.value === -1) negativeVotes++;
    else if (vote.value === 0) neutralVotes++;
    else if (vote.value === 1) positiveVotes++;
  }

  // Effective N (for Bayesian shrinkage)
  const effectiveN = totalWeight;

  // If below minimum threshold, no adjustment
  if (effectiveN < opts.minEffectiveN) {
    return {
      adjustedScore: baseScore,
      delta: 0,
      debug: {
        weightedMean: totalWeight > 0 ? weightedSum / totalWeight : 0,
        effectiveN,
        priorN: opts.priorN,
        shrunkSentiment: 0,
        agreement: 0,
        consensusMultiplier: 1,
        rawDelta: 0,
        cappedDelta: 0,
      },
    };
  }

  // Weighted mean sentiment
  const weightedMean = weightedSum / totalWeight;

  // Bayesian shrinkage toward 0 (no change)
  const shrunkSentiment = (effectiveN * weightedMean) / (effectiveN + opts.priorN);

  // Calculate consensus strength
  const totalVotes = votes.length;
  const pNegative = negativeVotes / totalVotes;
  const pPositive = positiveVotes / totalVotes;
  const agreement = Math.max(pNegative, pPositive);

  // Consensus multiplier
  let consensusMultiplier = 1;
  if (agreement >= opts.consensusBoost.start) {
    const excessAgreement = agreement - 0.5; // Beyond 50%
    consensusMultiplier = 1 + (opts.consensusBoost.strength * excessAgreement * 2);
  }

  // Apply diminishing returns through square root of effective N and tanh
  const sqrtN = Math.sqrt(effectiveN);
  const tanhInput = shrunkSentiment * opts.nonlinearityK;
  const tanhSentiment = Math.tanh(tanhInput);

  // Raw delta calculation
  const rawDelta = opts.responsiveness * sqrtN * tanhSentiment * consensusMultiplier;

  // Apply hard cap
  const cappedDelta = Math.max(-opts.maxMove, Math.min(opts.maxMove, rawDelta));

  const adjustedScore = baseScore + cappedDelta;

  return {
    adjustedScore,
    delta: cappedDelta,
    debug: {
      weightedMean,
      effectiveN,
      priorN: opts.priorN,
      shrunkSentiment,
      agreement,
      consensusMultiplier,
      rawDelta,
      cappedDelta,
    },
  };
}

/**
 * Helper function to convert player feedback to database storage format
 */
export function createPlayerVote(
  userId: string,
  playerId: string,
  voteValue: VoteValue,
  userTrust: number = 0.5
): PlayerVote {
  return {
    userId,
    value: voteValue,
    trust: userTrust,
    timestamp: Date.now(),
  };
}

/**
 * Helper function to calculate user trust score based on account age and activity
 */
export function calculateUserTrust(
  accountAgeInDays: number,
  totalVotes: number,
  verifiedEmail: boolean = false,
  premiumUser: boolean = false
): number {
  let trust = 0.3; // Base trust

  // Account age factor (max +0.3)
  const ageBonus = Math.min(0.3, accountAgeInDays / 365 * 0.3);
  trust += ageBonus;

  // Activity factor (max +0.2)
  const activityBonus = Math.min(0.2, Math.log(totalVotes + 1) / Math.log(100) * 0.2);
  trust += activityBonus;

  // Email verification (+0.1)
  if (verifiedEmail) trust += 0.1;

  // Premium user (+0.1)
  if (premiumUser) trust += 0.1;

  return Math.min(1.0, trust);
}